/**
 * Unified Data Collection System - Configuration
 * 
 * Each data type defines:
 * - Display properties (name, description, icon)
 * - Permissions (who can view, create, edit)
 * - Icon palettes for standardized data entry
 * - Structured fields for clean, queryable data
 * - Free-text fields for human observations
 * - Map styling and layer configuration
 * - Analytical lenses for AI interpretation
 */

export const DATA_TYPES = {
  // ===== CONSULTATION & PLANNING =====
  consultation: {
    id: 'consultation',
    name: 'Consultation Projects',
    description: 'Community consultation and engagement projects',
    category: 'planning',
    icon: '📋',
    color: '#4A90E2',
    
    permissions: {
      view: ['public'],
      create: ['staff', 'admin'],
      edit: ['staff', 'admin'],
      delete: ['admin'],
      comment: ['public'] // Anonymous commenting allowed
    },
    
    prompts: {
      welcome: 'Create a new consultation project',
      location: 'Where is this consultation project located? You can draw an area or place a marker.',
      details: 'Provide project details and documentation'
    },
    
    // No icon palette for consultation - uses traditional form
    iconPalette: null,
    
    structuredFields: [
      {
        name: 'title',
        type: 'text',
        label: 'Project Title',
        required: true,
        placeholder: 'Enter project title'
      },
      {
        name: 'description',
        type: 'textarea',
        label: 'Description',
        required: true,
        rows: 4,
        placeholder: 'Describe the consultation project'
      },
      {
        name: 'status',
        type: 'select',
        label: 'Status',
        required: true,
        options: ['Draft', 'Active', 'Completed', 'Cancelled'],
        default: 'Draft'
      },
      {
        name: 'startDate',
        type: 'date',
        label: 'Start Date',
        required: false
      },
      {
        name: 'endDate',
        type: 'date',
        label: 'End Date',
        required: false
      }
    ],
    
    freeTextFields: [
      {
        name: 'additionalNotes',
        label: 'Additional Notes',
        placeholder: 'Any additional information about this project...',
        rows: 3
      }
    ],
    
    fileUpload: {
      enabled: true,
      accept: '.pdf,.doc,.docx,.jpg,.jpeg,.png',
      multiple: false,
      label: 'Upload Document'
    },
    
    mapConfig: {
      markerIcon: '📋',
      markerColor: '#4A90E2',
      popupTemplate: (item) => `
        <strong>${item.title}</strong><br/>
        Status: ${item.structuredData?.status || 'N/A'}<br/>
        ${item.description ? item.description.substring(0, 100) + '...' : ''}
      `,
      clusterEnabled: false
    },
    
    analyticalLenses: ['spatial', 'temporal', 'community-engagement']
  },

  // ===== HARVESTING & TRADITIONAL USE =====
  'berry-harvesting': {
    id: 'berry-harvesting',
    name: 'Berry Harvesting',
    description: 'Map berry locations and traditional harvesting areas',
    category: 'harvesting',
    icon: '🫐',
    color: '#E63946',
    
    permissions: {
      view: ['public'],
      create: ['user', 'staff', 'admin'], // Logged-in users can contribute
      edit: ['self', 'staff', 'admin'], // Users can edit their own
      delete: ['self', 'admin'],
      approve: ['staff', 'admin'] // Staff approval for public display
    },
    
    prompts: {
      welcome: 'Help us map berry resources in traditional territory',
      location: 'Where did you find berry bushes? Draw the area or drop a pin.',
      iconPrompt: 'What type of berries did you find?',
      details: 'Tell us about the berries and harvesting conditions'
    },
    
    iconPalette: {
      selectionMode: 'single', // or 'multiple'
      label: 'Berry Type',
      required: true,
      icons: [
        { 
          id: 'BERRY_RASP',
          icon: '🫐',
          label: 'Raspberry',
          color: '#E63946',
          tooltip: 'Wild raspberries'
        },
        { 
          id: 'BERRY_BLUE',
          icon: '🫐',
          label: 'Blueberry',
          color: '#457B9D',
          tooltip: 'Wild blueberries'
        },
        { 
          id: 'BERRY_STRAW',
          icon: '🍓',
          label: 'Wild Strawberry',
          color: '#F72585',
          tooltip: 'Wild strawberries'
        },
        { 
          id: 'BERRY_BLACK',
          icon: '🫐',
          label: 'Blackberry',
          color: '#2B2D42',
          tooltip: 'Blackberries'
        },
        { 
          id: 'BERRY_CRAN',
          icon: '🫐',
          label: 'Cranberry',
          color: '#DC2F02',
          tooltip: 'Wild cranberries'
        },
        { 
          id: 'BERRY_OTHER',
          icon: '❓',
          label: 'Other',
          color: '#6C757D',
          tooltip: 'Other berry type (describe in notes)'
        }
      ]
    },
    
    structuredFields: [
      {
        name: 'abundance',
        type: 'select',
        label: 'Abundance',
        required: true,
        options: ['Sparse (few bushes)', 'Moderate (some bushes)', 'Abundant (many bushes)', 'Very Abundant (dense patch)'],
        default: 'Moderate (some bushes)'
      },
      {
        name: 'accessibility',
        type: 'select',
        label: 'Accessibility',
        required: true,
        options: ['Easy (roadside/trail)', 'Moderate (short hike)', 'Difficult (remote/rough terrain)'],
        default: 'Moderate (short hike)'
      },
      {
        name: 'harvestSeason',
        type: 'month-range',
        label: 'Best Picking Months',
        required: false,
        placeholder: 'e.g., June-August'
      },
      {
        name: 'bushHealth',
        type: 'select',
        label: 'Bush Health',
        required: false,
        options: ['Excellent', 'Good', 'Fair', 'Poor', 'Unknown']
      },
      {
        name: 'traditionalUse',
        type: 'checkbox',
        label: 'Traditional harvesting area',
        default: false
      }
    ],
    
    freeTextFields: [
      {
        name: 'observations',
        label: 'Observations & Traditional Knowledge',
        placeholder: 'Share any observations about wildlife, trail conditions, traditional knowledge, cultural significance...',
        rows: 4
      },
      {
        name: 'accessInstructions',
        label: 'How to Get There',
        placeholder: 'Directions, landmarks, parking information...',
        rows: 2
      }
    ],
    
    fileUpload: {
      enabled: true,
      accept: '.jpg,.jpeg,.png',
      multiple: true,
      maxFiles: 5,
      label: 'Upload Photos (optional)'
    },
    
    mapConfig: {
      markerIcon: '🫐',
      markerColor: '#E63946',
      popupTemplate: (item) => `
        <strong>${item.structuredData?.selectedIcon?.label || 'Berry Location'}</strong><br/>
        Abundance: ${item.structuredData?.abundance || 'N/A'}<br/>
        Season: ${item.structuredData?.harvestSeason || 'Unknown'}<br/>
        ${item.structuredData?.traditionalUse ? '⭐ Traditional Use Area' : ''}
      `,
      clusterEnabled: true,
      clusterRadius: 50
    },
    
    analyticalLenses: ['environmental', 'traditional-knowledge', 'access-rights', 'spatial', 'temporal']
  },

  // ===== GENERAL HARVESTING (Dynamic Categories) =====
  harvesting: {
    id: 'harvesting',
    name: 'Harvesting Data',
    description: 'Community harvesting locations and observations',
    category: 'harvesting',
    icon: '🌿',
    color: '#2E7D32',
    
    permissions: {
      view: ['public'],
      create: ['user', 'staff', 'admin'],
      edit: ['self', 'staff', 'admin'],
      delete: ['self', 'admin'],
      approve: ['staff', 'admin']
    },
    
    prompts: {
      welcome: 'Share your harvesting data with the community',
      location: 'Where is this harvesting location?',
      details: 'Provide details about what you found'
    },
    
    iconPalette: {
      'Plants & Medicine': '🌿',
      'Hunting': '🦌',
      'Birds': '🦅',
      'Fishing': '🎣',
      'Environmental Concerns': '⚠️',
      'Other': '📍',
      'default': '🌿'
    },
    
    structuredFields: [],
    freeTextFields: [
      {
        name: 'observations',
        label: 'Observations',
        placeholder: 'Describe what you found, accessibility, seasonal notes, etc.',
        required: false
      }
    ],
    
    mapDisplay: {
      markerType: 'icon',
      iconSelector: (item) => {
        // Use category-specific icon
        const category = item.structuredData?.category;
        return category || 'default';
      },
      popupTemplate: (item) => `
        <strong>${item.structuredData?.category || 'Harvesting'} - ${item.structuredData?.subcategory || 'General'}</strong><br/>
        ${item.freeTextData?.observations ? item.freeTextData.observations.substring(0, 100) + '...' : ''}
      `,
      clusterEnabled: true,
      clusterRadius: 50
    },
    
    analyticalLenses: ['spatial', 'temporal', 'traditional-knowledge']
  },

  // ===== WILDLIFE OBSERVATION =====
  'moose-sightings': {
    id: 'moose-sightings',
    name: 'Moose Sightings',
    description: 'Track moose populations and movement patterns',
    category: 'wildlife',
    icon: '🦌',
    color: '#6F4E37',
    
    permissions: {
      view: ['public'],
      create: ['user', 'staff', 'admin'],
      edit: ['self', 'staff', 'admin'],
      delete: ['self', 'admin'],
      approve: ['staff', 'admin']
    },
    
    prompts: {
      welcome: 'Report a moose sighting to help track wildlife populations',
      location: 'Where did you see the moose? Place a marker at the sighting location.',
      iconPrompt: 'How many moose did you see?',
      details: 'Provide details about the sighting'
    },
    
    iconPalette: {
      selectionMode: 'single',
      label: 'Number of Moose',
      required: true,
      icons: [
        { id: 'MOOSE_SINGLE', icon: '🦌', label: 'Single Moose', color: '#6F4E37' },
        { id: 'MOOSE_PAIR', icon: '🦌🦌', label: 'Pair (2)', color: '#8B6F47' },
        { id: 'MOOSE_SMALL', icon: '🦌👥', label: 'Small Group (3-5)', color: '#A0826D' },
        { id: 'MOOSE_LARGE', icon: '🦌👥👥', label: 'Large Group (6+)', color: '#B8956A' }
      ]
    },
    
    structuredFields: [
      {
        name: 'date',
        type: 'datetime-local',
        label: 'Sighting Date & Time',
        required: true,
        default: 'now'
      },
      {
        name: 'mooseType',
        type: 'checkbox-group',
        label: 'Moose Type (check all that apply)',
        options: ['Bull', 'Cow', 'Calf'],
        required: false
      },
      {
        name: 'behavior',
        type: 'select',
        label: 'Behavior',
        options: ['Grazing/Feeding', 'Walking/Moving', 'Standing/Resting', 'Running', 'Swimming', 'Aggressive', 'Other'],
        required: false
      },
      {
        name: 'proximity',
        type: 'select',
        label: 'Proximity to Road/Trail',
        options: ['On road/trail', 'Near (< 50m)', 'Medium distance (50-200m)', 'Far (> 200m)'],
        required: false
      },
      {
        name: 'vehicleCollisionRisk',
        type: 'checkbox',
        label: 'Potential vehicle collision risk',
        default: false
      }
    ],
    
    freeTextFields: [
      {
        name: 'description',
        label: 'Sighting Description',
        placeholder: 'Describe what you observed, moose behavior, habitat conditions...',
        rows: 4
      }
    ],
    
    fileUpload: {
      enabled: true,
      accept: '.jpg,.jpeg,.png,.mp4,.mov',
      multiple: true,
      maxFiles: 3,
      label: 'Upload Photos/Video (optional)'
    },
    
    mapConfig: {
      markerIcon: '🦌',
      markerColor: '#6F4E37',
      popupTemplate: (item) => `
        <strong>Moose Sighting</strong><br/>
        Count: ${item.structuredData?.selectedIcon?.label || 'N/A'}<br/>
        Date: ${item.structuredData?.date ? new Date(item.structuredData.date).toLocaleDateString() : 'N/A'}<br/>
        ${item.structuredData?.vehicleCollisionRisk ? '⚠️ Collision Risk' : ''}
      `,
      clusterEnabled: true,
      clusterRadius: 40,
      heatmapEnabled: true // For population density visualization
    },
    
    analyticalLenses: ['environmental', 'spatial', 'temporal', 'public-safety']
  },

  // ===== ENVIRONMENTAL HAZARDS =====
  'environmental-hazards': {
    id: 'environmental-hazards',
    name: 'Environmental Hazards',
    description: 'Report environmental hazards and safety concerns',
    category: 'safety',
    icon: '⚠️',
    color: '#FFA500',
    
    permissions: {
      view: ['public'],
      create: ['user', 'staff', 'admin'],
      edit: ['self', 'staff', 'admin'],
      delete: ['self', 'admin'],
      approve: ['staff', 'admin'],
      urgent: ['staff', 'admin'] // Flag as urgent
    },
    
    prompts: {
      welcome: 'Report environmental hazards to help keep the community safe',
      location: 'Where is the hazard located? Place a marker or draw the affected area.',
      iconPrompt: 'What type of hazard are you reporting?',
      details: 'Describe the hazard and any immediate risks'
    },
    
    iconPalette: {
      selectionMode: 'single',
      label: 'Hazard Type',
      required: true,
      icons: [
        { id: 'HAZ_FIRE', icon: '🔥', label: 'Fire Risk', color: '#FF4500' },
        { id: 'HAZ_FLOOD', icon: '🌊', label: 'Flooding', color: '#4682B4' },
        { id: 'HAZ_EROSION', icon: '⛰️', label: 'Erosion/Landslide', color: '#8B4513' },
        { id: 'HAZ_POLLUTION', icon: '☣️', label: 'Pollution/Contamination', color: '#9400D3' },
        { id: 'HAZ_WILDLIFE', icon: '🐻', label: 'Wildlife Danger', color: '#8B0000' },
        { id: 'HAZ_INFRA', icon: '🚧', label: 'Infrastructure Damage', color: '#FFA500' },
        { id: 'HAZ_TREE', icon: '🌲', label: 'Dangerous Tree/Branch', color: '#228B22' },
        { id: 'HAZ_OTHER', icon: '⚠️', label: 'Other Hazard', color: '#FF6347' }
      ]
    },
    
    structuredFields: [
      {
        name: 'severity',
        type: 'select',
        label: 'Severity',
        required: true,
        options: ['Low (awareness)', 'Medium (caution needed)', 'High (avoid area)', 'Critical (immediate danger)'],
        default: 'Medium (caution needed)'
      },
      {
        name: 'reportDate',
        type: 'datetime-local',
        label: 'When did you observe this?',
        required: true,
        default: 'now'
      },
      {
        name: 'affectedArea',
        type: 'select',
        label: 'Affected Area Type',
        options: ['Road', 'Trail', 'Waterway', 'Building/Structure', 'Forest', 'Open Area', 'Other'],
        required: false
      },
      {
        name: 'reportedToAuthorities',
        type: 'checkbox',
        label: 'Already reported to authorities',
        default: false
      },
      {
        name: 'requiresImmediateAction',
        type: 'checkbox',
        label: 'Requires immediate action',
        default: false
      }
    ],
    
    freeTextFields: [
      {
        name: 'description',
        label: 'Hazard Description',
        placeholder: 'Describe the hazard, location details, immediate risks, suggested actions...',
        rows: 5,
        required: true
      },
      {
        name: 'accessRoute',
        label: 'How to Access (for response teams)',
        placeholder: 'Directions for emergency responders or maintenance crews...',
        rows: 2
      }
    ],
    
    fileUpload: {
      enabled: true,
      accept: '.jpg,.jpeg,.png,.mp4,.mov',
      multiple: true,
      maxFiles: 5,
      label: 'Upload Photos/Video Evidence'
    },
    
    mapConfig: {
      markerIcon: '⚠️',
      markerColor: '#FFA500',
      popupTemplate: (item) => `
        <strong>${item.structuredData?.selectedIcon?.label || 'Hazard'}</strong><br/>
        Severity: ${item.structuredData?.severity || 'N/A'}<br/>
        ${item.structuredData?.requiresImmediateAction ? '🚨 <strong>IMMEDIATE ACTION NEEDED</strong><br/>' : ''}
        ${item.description ? item.description.substring(0, 100) + '...' : ''}
      `,
      clusterEnabled: false, // Show all hazards individually
      urgentHighlight: true // Highlight critical/urgent items
    },
    
    analyticalLenses: ['environmental', 'public-safety', 'infrastructure', 'spatial', 'temporal']
  }
};

/**
 * Get data type configuration by ID
 */
export const getDataType = (dataTypeId) => {
  // Check static types first, then dynamic types
  return DATA_TYPES[dataTypeId] || dynamicDataTypes[dataTypeId] || null;
};

/**
 * Get all data types in a category
 */
export const getDataTypesByCategory = (category) => {
  return Object.values(DATA_TYPES).filter(dt => dt.category === category);
};

/**
 * Check if user has permission for action on data type
 */
export const hasPermission = (dataType, action, userRole, isOwner = false) => {
  const permissions = dataType.permissions[action];
  if (!permissions) return false;
  
  // Check for 'public' permission
  if (permissions.includes('public')) return true;
  
  // Check for 'self' permission (user is owner)
  if (permissions.includes('self') && isOwner) return true;
  
  // Check user role
  return permissions.includes(userRole);
};

/**
 * Get display color for data type
 */
export const getDataTypeColor = (dataTypeId) => {
  const dataType = DATA_TYPES[dataTypeId];
  return dataType ? dataType.color : '#999999';
};

/**
 * Get icon for data item based on structured data
 */
export const getItemIcon = (dataTypeId, item) => {
  const dataType = DATA_TYPES[dataTypeId];
  if (!dataType) return '📍';
  
  // If item has selected icon from palette, use that
  if (item.structuredData?.selectedIcon) {
    return item.structuredData.selectedIcon.icon;
  }
  
  // Otherwise use data type default icon
  return dataType.icon;
};

/**
 * Fetch and merge dynamic data types from backend
 */
let dynamicDataTypes = {};
let dynamicDataTypesLoaded = false;

export const loadDynamicDataTypes = async () => {
  if (dynamicDataTypesLoaded) return dynamicDataTypes;
  
  try {
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
    const response = await fetch(`${API_URL}/api/admin/data-types`);
    const data = await response.json();
    
    if (data.success && data.dataTypes) {
      // Convert backend format to frontend format and merge
      data.dataTypes.forEach(dt => {
        dynamicDataTypes[dt.id] = dt;
      });
      dynamicDataTypesLoaded = true;
    }
  } catch (error) {
    console.error('Error loading dynamic data types:', error);
  }
  
  return dynamicDataTypes;
};

/**
 * Get all data types (static + dynamic)
 */
export const getAllDataTypes = async () => {
  await loadDynamicDataTypes();
  return { ...DATA_TYPES, ...dynamicDataTypes };
};

export default DATA_TYPES;
